import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Mail,
  Phone,
  Key,
  Shield,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle2,
  Building2,
  Percent,
  Info,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as resellerService from "@/shared/api/services/resellerService";
import { useToast } from "@/shared/components/ui/use-toast";
import { Loader2 } from "lucide-react";

export default function ResellerSettings() {
  const { t } = useLanguage();
  const resellerName = "Asia Gaming Network";

  const { data: resellerProfile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: () => resellerService.getProfile(),
  });

  const [reseller, setReseller] = useState({
    name: "",
    code: "",
    email: "",
    support_email: "",
    contact_phone: "",
    api_key: "",
    secret_key: "",
  });

  useEffect(() => {
    if (resellerProfile) {
      setReseller({
        name: resellerProfile.name || "",
        code: resellerProfile.code || "",
        email: resellerProfile.email || "",
        support_email: resellerProfile.supportEmail || "",
        contact_phone: resellerProfile.contactPhone || "",
        api_key: resellerProfile.apiKey || "",
        secret_key: resellerProfile.secretKey || "",
      });
    }
  }, [resellerProfile]);

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [regenerateDialog, setRegenerateDialog] = useState(false);
  const [copied, setCopied] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRegenerate = async () => {
    setLoadingKeys(true);
    try {
      const newKey = "rsl_pk_" + Array.from({ length: 32 }, () =>
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random() * 62)]
      ).join("");
      const newSecret = "rsl_sk_" + Array.from({ length: 32 }, () =>
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random() * 62)]
      ).join("");
      
      await resellerService.updateProfile({
        apiKey: newKey,
        secretKey: newSecret,
      });

      queryClient.invalidateQueries({ queryKey: ["reseller-profile"] });
      toast({
        title: t("success"),
        description: t("keysRegeneratedSuccess") || "API keys regenerated successfully",
      });
      setRegenerateDialog(false);
    } catch (error) {
      toast({
        title: t("error"),
        description: error.message || t("failedToRegenerate"),
        variant: "destructive",
      });
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await resellerService.updateProfile({
        name: reseller.name,
        supportEmail: reseller.support_email,
        contactPhone: reseller.contact_phone,
      });
      queryClient.invalidateQueries({ queryKey: ["reseller-profile"] });
      toast({
        title: t("success"),
        description: t("profileUpdatedSuccess") || "Profile updated successfully",
      });
    } catch (error) {
      toast({
        title: t("error"),
        description: error.message || t("failedToUpdate"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResellerLayout currentPage="ResellerSettings" resellerName={reseller.name || t("loading")}>
      <div className="space-y-6 max-w-3xl mx-auto">
        <div className="text-left">
          <h1 className="text-2xl font-bold text-slate-900">{t("settings")}</h1>
          <p className="text-slate-500">{t("manageProfile")}</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t("resellerProfile")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("businessName")}</Label>
                  <Input value={reseller.name} onChange={(e) => setReseller(prev => ({ ...prev, name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>{t("code")}</Label>
                  <Input value={reseller.code} disabled className="bg-slate-50" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("email")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="email" value={reseller.email} disabled onChange={(e) => setReseller(prev => ({ ...prev, email: e.target.value }))} className="pl-10 bg-slate-5" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("supportEmail")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="email" value={reseller.support_email} onChange={(e) => setReseller(prev => ({ ...prev, support_email: e.target.value }))} className="pl-10" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("phone")}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={reseller.contact_phone} onChange={(e) => setReseller(prev => ({ ...prev, contact_phone: e.target.value }))} className="pl-10" />
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 min-w-[140px]">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("saving") || "Saving..."}
                  </>
                ) : (
                  t("saveChanges")
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardHeader className="flex flex-row items-center gap-4 space-y-0 pb-6">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center shadow-sm">
                <Key className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl">{t("apiKeys") || "API Keys"}</CardTitle>
                <CardDescription>{t("apiKeysDesc") || "Your authentication credentials for API access"}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* API Key (Public) */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-900">{t("apiKeyPublic") || "API Key (Public)"}</Label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <div className="p-4 bg-slate-50/50 border border-slate-100 rounded-2xl font-mono text-sm text-slate-700 overflow-x-hidden whitespace-nowrap pr-12">
                      {showApiKey ? reseller.api_key : (reseller.api_key ? `${reseller.api_key.slice(0, 12)}${"•".repeat(20)}${reseller.api_key.slice(-4)}` : "••••••••••••••••")}
                    </div>
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showApiKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <Button variant="outline" size="icon" className="h-12 w-12 shrink-0 rounded-xl" onClick={() => copyToClipboard(reseller.api_key, "api")}>
                    {copied === "api" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Copy className="h-5 w-5 text-slate-500" />}
                  </Button>
                </div>
              </div>

              {/* Secret Key */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-900">{t("secretKey")}</Label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <div className="p-4 bg-slate-50/50 border border-slate-100 rounded-2xl font-mono text-sm text-slate-700 overflow-x-hidden whitespace-nowrap pr-12">
                      {showSecretKey ? reseller.secret_key : (reseller.secret_key ? `${reseller.secret_key.slice(0, 12)}${"•".repeat(20)}${reseller.secret_key.slice(-4)}` : "••••••••••••••••")}
                    </div>
                    <button
                      onClick={() => setShowSecretKey(!showSecretKey)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showSecretKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <Button variant="outline" size="icon" className="h-12 w-12 shrink-0 rounded-xl" onClick={() => copyToClipboard(reseller.secret_key, "secret")}>
                    {copied === "secret" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Copy className="h-5 w-5 text-slate-500" />}
                  </Button>
                </div>
              </div>

              {/* Regenerate Section */}
              <div className="pt-6 border-t border-slate-100 flex justify-between items-center">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-slate-900">{t("regenerateKeys") || "Regenerate Keys"}</h4>
                  <p className="text-xs text-slate-500">{t("regenerateWarning") || "This will invalidate your current keys"}</p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setRegenerateDialog(true)} 
                  disabled={loadingKeys}
                  className="gap-2 text-amber-600 border-slate-200 hover:bg-amber-50 hover:text-amber-700 rounded-xl font-bold h-11 min-w-[130px]"
                >
                  {loadingKeys ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {loadingKeys ? t("processing") || "Processing..." : (t("regenerate") || "Regenerate")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Fees & Commission Card */}
        {resellerProfile && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardHeader className="flex flex-row items-center gap-4 space-y-0">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center shadow-sm">
                  <Percent className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">{t("feesAndCommission")}</CardTitle>
                  <CardDescription>{t("feesAndCommissionDesc")}</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Parent Commission Info */}
                {resellerProfile.parentName && (
                  <div className="p-6 bg-blue-50/50 border border-blue-100 rounded-2xl">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <Info className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                          {t("givenBy").replace("{type}", resellerProfile.parentType).replace("{name}", resellerProfile.parentName)}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                          <div>
                            <p className="text-blue-600 text-sm font-medium mb-1">{t("depositCommission")}</p>
                            <p className="text-blue-900 text-2xl font-bold">
                              {resellerProfile.parentDepositCommission ?? "0"}%
                            </p>
                          </div>
                          <div>
                            <p className="text-blue-600 text-sm font-medium mb-1">{t("withdrawalCommission")}</p>
                            <p className="text-blue-900 text-2xl font-bold">
                              {resellerProfile.parentWithdrawalCommission ?? "0"}%
                            </p>
                          </div>
                          <div>
                            <p className="text-blue-600 text-sm font-medium mb-1">{t("percentageFee")}</p>
                            <p className="text-blue-900 text-2xl font-bold">
                              {resellerProfile.parentPercentageFee ?? "0"}%
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Commission Rates (Visual Cards) */}
                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">{t("yourCommissionRates")}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">{t("yourCommissionRatesDesc")}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Deposit Commission Card */}
                    <div className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100 flex flex-col gap-3">
                      <span className="text-emerald-700 text-sm font-semibold">{t("depositCommission")}</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-slate-900 text-3xl font-bold">{resellerProfile.depositCommission ?? "0"}</span>
                        <span className="text-emerald-600 font-medium">%</span>
                      </div>
                    </div>

                    {/* Withdrawal Commission Card */}
                    <div className="p-4 rounded-2xl bg-blue-50/50 border border-blue-100 flex flex-col gap-3">
                      <span className="text-blue-700 text-sm font-semibold">{t("withdrawalCommission")}</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-slate-900 text-3xl font-bold">{resellerProfile.withdrawalCommission ?? "0"}</span>
                        <span className="text-blue-600 font-medium">%</span>
                      </div>
                    </div>

                    {/* Percentage Fee Card */}
                    <div className="p-4 rounded-2xl bg-purple-50/50 border border-purple-100 flex flex-col gap-3">
                      <span className="text-purple-700 text-sm font-semibold">{t("percentageFeePercent") || "Percentage Fee (%)"}</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-slate-900 text-3xl font-bold">{resellerProfile.percentageFee ?? "0"}</span>
                        <span className="text-purple-600 font-medium">%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}


        <AlertDialog open={regenerateDialog} onOpenChange={setRegenerateDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("regenerateApiKeys")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("invalidateImmediately")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={handleRegenerate} className="bg-red-600 hover:bg-red-700">
                {t("regenerate")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ResellerLayout>
  );
}